import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './FilledQuestionnaireItem.module.scss';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import { useAppContext } from '../../../../Services/AppContext';
import moment from 'moment';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import Button from '../../../../Components/Button/Button';
import { FilledQuestionnaireSelectedItem } from './FilledQuestionnaireList';
import NetteApiClient from '../../../../Services/NetteApiClient';
import { joinClasses } from '../../../../Utils/className.util';
import TreatmentPatientButton from '../../../../Components/Treatment/TreatmentPatientButton';
import useTranslation from '../../../../Hooks/useTranslation';
import useQuestionnaireResults from '../../Hooks/useQuestionnaireResults';
import QuestionnaireResultsModal from '../QuestionnaireResults/QuestionnaireResultsModal';
import { User } from '../../../../graphql/graphql';
import usePatientIdentification from '../../../Patient/Hooks/usePatientIdentification';

interface FilledQuestionnaireItemProps {
	id: string;
	createdAt: string;
	patient: User;
	patientName: string;
	patientBirthNumber: string;
	patientId: string;
	doctor: string;
	questionnaire: string;
	questionnaireId: string;
	questionnaireCode: string;
	isSeen: boolean;
	result?: {
		icon: string;
		text: string;
		theme: 'red' | 'orange' | 'green';
		values: string;
	};
	isSeenMarkedBy?: string;
	isSelected: boolean;
	onSelect: (item: FilledQuestionnaireSelectedItem, add: boolean) => void;
	reloadList?: () => void;
}

const RESULTS = {
	red: {
		icon: '/dist/icons/alert-red.svg',
	},
	orange: {
		icon: '/dist/icons/alert-orange.svg',
	},
	green: {
		icon: '/dist/icons/check-green.svg',
	},
};
const FilledQuestionnaireItem: React.FC<FilledQuestionnaireItemProps> = ({
	id,
	createdAt,
	patient,
	patientName,
	patientId,
	patientBirthNumber,
	doctor,
	questionnaire,
	questionnaireId,
	questionnaireCode,
	isSelected,
	isSeen,
	onSelect,
	result,
	reloadList,
	isSeenMarkedBy,
}) => {
	const { dateFormatMoment } = useAppContext();
	const { identification } = usePatientIdentification(patient as User);
	const { t } = useTranslation();
	const [isChecked, setIsChecked] = useState(false);
	const [isResultOpen, setIsResultOpen] = useState(false);
	const { markAsUnRead } = useQuestionnaireResults();
	// @ts-ignore
	const { redirectPatientMonitoringList, workplaceId, userData } =
		useAppContext();

	useEffect(() => {
		setIsChecked(isSelected);
	}, [isSelected]);

	useEffect(() => {
		onSelect({ id, questionnaireId }, isChecked);
	}, [isChecked]);

	const handleCheckItem = () => {
		setIsChecked(prev => !prev);
	};

	const handleOpenResult = async () => {
		setIsResultOpen(true);
	};

	const handleCloseResult = () => {
		setIsResultOpen(false);
		reloadList?.();
	};

	const handleOpenPatientMonitoringList = () => {
		NetteApiClient.get(
			redirectPatientMonitoringList.replace('__PATIENT-ID__', patientId),
		);
	};

	const handleUnseenItem = async (
		questionnaireId: string,
		resultId: string,
	) => {
		setIsChecked(false);
		await markAsUnRead(questionnaireId, resultId);
		reloadList?.();
	};

	return (
		<>
			<div
				className={joinClasses([
					styles.item,
					result ? styles[result.theme] : undefined,
				])}>
				<div className={styles.patient}>
					{isSeen ? (
						<div onClick={() => handleUnseenItem(questionnaireId, id)}>
							<img
								src={'/dist/icons/eye.svg'}
								alt={'eye'}
								width='17px'
								style={{ marginRight: '8px', cursor: 'pointer' }}
							/>
						</div>
					) : (
						<Checkbox value={isChecked} onChange={handleCheckItem} />
					)}
					<div className={styles.patientData}>
						<div className={styles.text}>
							{moment(createdAt).format(dateFormatMoment + ' HH:mm')}
						</div>
						<TreatmentPatientButton
							text={patientName}
							patientId={patientId}
							workplaceId={workplaceId}
							loggedUserId={userData.id}
							className={styles.title}
						/>
						<div className={styles.extra}>
							{identification.value && (
								<div className={styles.name}>
									<span>{t(identification.title)}: </span>
									{identification.value}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className={styles.content}>
					<div>
						<div className={styles.title}>{questionnaire}</div>
					</div>
					{result && (
						<div className={joinClasses([styles.name, styles[result.theme]])}>
							<img src={RESULTS?.[result.theme]?.icon} />
							<span>{t('base.risk')}: </span>
							{result.text}
						</div>
					)}
					{doctor && (
						<div className={styles.extra}>
							<div className={styles.name}>
								<span>{t('base.sentBy')}: </span>
								{doctor}
							</div>
						</div>
					)}
					{isSeen && (
						<div className={styles.extra}>
							<div className={styles.name}>
								<span>{t('base.seenBy')}: </span>
								{isSeenMarkedBy}
							</div>
						</div>
					)}
				</div>
				<div className={styles.controls}>
					<Button
						title='base.result'
						theme={Theme.GREEN}
						onClick={handleOpenResult}
					/>
					<LinkButton
						title='base.plannedMonitorings'
						theme={Theme.BLUE}
						onClick={handleOpenPatientMonitoringList}
					/>
				</div>
			</div>
			<QuestionnaireResultsModal
				questionnaireCode={questionnaireCode}
				questionnaireName={questionnaire}
				patientId={patientId}
				isOpen={isResultOpen}
				onClose={handleCloseResult}
			/>
		</>
	);
};

export default FilledQuestionnaireItem;
